/**
 * Client de l'assistant de recherche (add-on 24) : réponses validées par les
 * schémas partagés. Aucune question ni réponse n'entre dans le cache de
 * requêtes : l'échange vit dans l'écran, une mutation sans mémoire.
 */
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { modificationReglagesRechercheSchema } from '@artosera/shared';
import {
  reponseReglagesRechercheSchema,
  reponseRechercheSchema,
  type MOTIFS_RETOUR_RECHERCHE,
} from '@artosera/shared';
import type { z } from 'zod';
import { api, type AppelApi } from './client.js';
import { appelDeRequete, cleMetier, dansLaPortee } from './cles.js';
import { estApiError } from './errors.js';
import { useMutation } from './mutation.js';

export type ReponseQuestion = z.infer<typeof reponseRechercheSchema>;
export type ReglagesRecherche = z.infer<typeof reponseReglagesRechercheSchema>;
export type ModificationReglagesRecherche = z.input<typeof modificationReglagesRechercheSchema>;
export type MotifRetour = (typeof MOTIFS_RETOUR_RECHERCHE)[number];

export interface Echange {
  readonly question: string;
  readonly reponse: string;
}

export interface CorpsQuestion {
  readonly question: string;
  readonly historique: readonly Echange[];
  readonly approfondir?: boolean;
  /** Une clé par geste : un rejeu réseau dans la minute ne coûte pas deux fois. */
  readonly cle: string;
}

const chemin = (g: string, suite: string) =>
  `/g/${encodeURIComponent(g)}/assistant-recherche${suite}`;

export const assistantRechercheApi = {
  poser: async (g: string, { cle, ...corps }: CorpsQuestion, appel?: AppelApi) =>
    reponseRechercheSchema.parse(
      await api.post(chemin(g, '/questions'), corps, { galerie: g, cleOperation: cle, ...appel }),
    ),
  retour: (
    g: string,
    appelId: string,
    corps: { utile: boolean; motif?: MotifRetour },
    appel?: AppelApi,
  ) =>
    api.post<undefined>(chemin(g, `/questions/${encodeURIComponent(appelId)}/retour`), corps, {
      galerie: g,
      ...appel,
    }),
  reglages: async (g: string, appel?: AppelApi) =>
    reponseReglagesRechercheSchema.parse(
      await api.get(chemin(g, '/reglages'), { galerie: g, ...appel }),
    ),
  modifier: async (g: string, corps: ModificationReglagesRecherche, appel?: AppelApi) =>
    reponseReglagesRechercheSchema.parse(
      await api.patch(chemin(g, '/reglages'), corps, { galerie: g, ...appel }),
    ),
};

export function useReglagesRecherche(g: string, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: cleMetier(g, 'assistant-recherche', 'reglages'),
    queryFn: (c) => assistantRechercheApi.reglages(g, appelDeRequete(c)),
    ...options,
  });
}

export function useModifierReglagesRecherche(g: string) {
  const client = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: (corps: ModificationReglagesRecherche, appel) =>
        assistantRechercheApi.modifier(g, corps, appel),
      onSuccess: (reglages) =>
        client.setQueryData(cleMetier(g, 'assistant-recherche', 'reglages'), reglages),
    }),
  );
}

export function usePoserQuestion(g: string) {
  return useMutation(
    dansLaPortee({
      mutationFn: (corps: CorpsQuestion, appel) => assistantRechercheApi.poser(g, corps, appel),
    }),
  );
}

export function useRetourQuestion(g: string) {
  return useMutation(
    dansLaPortee({
      mutationFn: (v: { appelId: string; utile: boolean; motif?: MotifRetour }, appel) =>
        assistantRechercheApi.retour(g, v.appelId, { utile: v.utile, motif: v.motif }, appel),
    }),
  );
}

/** Ce qu'un échec de question veut dire pour l'écran (états de la spec, § 10). */
export type EchecQuestion =
  | { readonly nature: 'plafond' }
  | { readonly nature: 'quota'; readonly secondes: number | null }
  | { readonly nature: 'coupe' }
  | { readonly nature: 'role' }
  | { readonly nature: 'indisponible' }
  | { readonly nature: 'reseau' };

export function echecDeQuestion(erreur: unknown): EchecQuestion {
  if (!estApiError(erreur)) return { nature: 'reseau' };
  const details = (erreur.details ?? {}) as { raison?: unknown; reessayerDans?: unknown };
  const raison = typeof details.raison === 'string' ? details.raison : '';
  if (raison === 'ia_plafond') return { nature: 'plafond' };
  if (erreur.status === 429)
    return {
      nature: 'quota',
      secondes: typeof details.reessayerDans === 'number' ? details.reessayerDans : null,
    };
  if (raison === 'assistant_recherche_coupe') return { nature: 'coupe' };
  if (raison === 'role_non_autorise') return { nature: 'role' };
  return { nature: 'indisponible' };
}
